import React from 'react';
import { NeuralPrepData } from '../../types/clientIntelligence';
import { formatNeuralPrepText, generateEmailPrep, generatePrintSheet } from '../../utils/emailPrepUtils';

interface NeuralPrepModalProps {
  isOpen: boolean;
  onClose: () => void;
  neuralPrep: any;
  clientName: string;
  loading: boolean;
}

export const NeuralPrepModal: React.FC<NeuralPrepModalProps> = ({
  isOpen,
  onClose,
  neuralPrep,
  clientName,
  loading
}) => {
  if (!isOpen) return null;

  const handleCopyPrep = async () => {
    if (!neuralPrep) return;
    
    try {
      const formattedText = formatNeuralPrepText(neuralPrep, clientName);
      await navigator.clipboard.writeText(formattedText);
      alert('Neural prep copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('Failed to copy to clipboard');
    }
  };

  const handleEmailPrep = () => {
    if (!neuralPrep) return;
    generateEmailPrep(neuralPrep, clientName);
  };

  const handlePrintSheet = () => {
    if (!neuralPrep) return;
    generatePrintSheet(neuralPrep, clientName);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full my-8 shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-3xl">🧠</div>
              <div>
                <h2 className="text-2xl font-bold">Neural Prep</h2>
                <p className="text-purple-100">Smart relationship intelligence for {clientName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-purple-200 transition-colors p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          {/* Action Buttons in Header */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleCopyPrep}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
              </svg>
              Copy Prep
            </button>
            <button
              onClick={handleEmailPrep}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              Email Prep
            </button>
            <button
              onClick={handlePrintSheet}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
              </svg>
              Print Sheet
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-lg text-gray-600">Generating neural prep...</span>
            </div>
          ) : neuralPrep ? (
            <div className="space-y-6">
              {/* Domain Intelligence Badge */}
              <div className="flex items-center gap-3 mb-6">
                <div className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full">
                  <span className="text-sm font-medium text-purple-800">
                    🎯 Domain Intelligence: {neuralPrep.domainAnalysis?.primaryDomain || 'Finance'}
                  </span>
                </div>
                <div className="px-3 py-1 bg-gray-100 rounded-full">
                  <span className="text-xs text-gray-600">Generated: {new Date().toLocaleDateString()}</span>
                </div>
              </div>

              {/* Personal Connections */}
              {neuralPrep.personalConnections && neuralPrep.personalConnections.length > 0 && (
                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
                    👥 Personal Connection Points
                  </h3>
                  <div className="space-y-3">
                    {neuralPrep.personalConnections.map((connection: any, index: number) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-green-100">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                        <div>
                          <div className="font-medium text-green-800">{connection.topic}</div>
                          <div className="text-sm text-green-600">{connection.context}</div>
                          {connection.emotionalContext && (
                            <div className="text-xs text-green-500 mt-1">
                              <strong>Emotional Context:</strong> {connection.emotionalContext}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Conversation Starters */}
              {neuralPrep.conversationStarters && neuralPrep.conversationStarters.length > 0 && (
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-800 mb-4 flex items-center gap-2">
                    💼 AI-Enhanced Conversation Starters
                  </h3>
                  <div className="space-y-3">
                    {neuralPrep.conversationStarters.map((starter: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg border border-purple-100 p-4">
                        <div className="flex items-start gap-3 mb-2">
                          <div className="text-purple-500 font-bold">Q:</div>
                          <div className="text-purple-700 italic font-medium">
                            "{starter.question || starter}"
                          </div>
                        </div>
                        {starter.context && (
                          <div className="ml-6 text-sm text-purple-600 mb-2">
                            <strong>Context:</strong> {starter.context}
                          </div>
                        )}
                        {starter.followUp && (
                          <div className="ml-6 text-sm text-purple-500">
                            <strong>Follow-up:</strong> {starter.followUp}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strategic Points */}
              {neuralPrep.strategicPoints && neuralPrep.strategicPoints.length > 0 && (
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200 p-6">
                  <h3 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                    🎯 Strategic Talking Points
                  </h3>
                  <div className="space-y-3">
                    {neuralPrep.strategicPoints.map((point: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-purple-100">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-purple-700">{point.category}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            point.priority === 'High' ? 'bg-red-100 text-red-700' :
                            point.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {point.priority || 'Medium'} Priority
                          </span>
                        </div>
                        <p className="text-sm text-purple-600 mb-2">{point.details}</p>
                        {point.reasoning && (
                          <p className="text-xs text-purple-500">
                            <strong>Reasoning:</strong> {point.reasoning}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              {neuralPrep.actionItems && neuralPrep.actionItems.length > 0 && (
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg border border-orange-200 p-6">
                  <h3 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                    ⚡ Immediate Action Items
                  </h3>
                  <div className="space-y-3">
                    {neuralPrep.actionItems.map((item: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-orange-100">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-orange-700">{item.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.priority === 'High' ? 'bg-red-100 text-red-700' :
                            item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {item.priority || 'Medium'}
                          </span>
                        </div>
                        <p className="text-sm text-orange-600 mb-2">{item.description}</p>
                        {item.deadline && (
                          <p className="text-xs text-orange-500 mb-1">
                            <strong>Deadline:</strong> {item.deadline}
                          </p>
                        )}
                        {item.reasoning && (
                          <p className="text-xs text-orange-500">
                            <strong>Reasoning:</strong> {item.reasoning}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Insights */}
              {neuralPrep.aiInsights && (
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                    🎭 AI Personality Insights
                  </h3>
                  
                  <div className="space-y-4">
                    {neuralPrep.aiInsights.personalityProfile && (
                      <div className="bg-white rounded-lg p-4 border border-blue-100">
                        <h4 className="font-semibold text-blue-800 mb-2">Personality Profile</h4>
                        <p className="text-blue-700 text-sm">{neuralPrep.aiInsights.personalityProfile}</p>
                      </div>
                    )}
                    
                    {neuralPrep.aiInsights.communicationStyle && (
                      <div className="bg-white rounded-lg p-4 border border-blue-100">
                        <h4 className="font-semibold text-blue-800 mb-2">Communication Style</h4>
                        <p className="text-blue-700 text-sm">{neuralPrep.aiInsights.communicationStyle}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {neuralPrep.aiInsights.motivationFactors && neuralPrep.aiInsights.motivationFactors.length > 0 && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <h5 className="font-medium text-blue-800 mb-2">Motivation Factors</h5>
                          <ul className="text-xs text-blue-600 space-y-1">
                            {neuralPrep.aiInsights.motivationFactors.map((factor: string, index: number) => (
                              <li key={index}>• {factor}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {neuralPrep.aiInsights.concernAreas && neuralPrep.aiInsights.concernAreas.length > 0 && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <h5 className="font-medium text-blue-800 mb-2">Concern Areas</h5>
                          <ul className="text-xs text-blue-600 space-y-1">
                            {neuralPrep.aiInsights.concernAreas.map((concern: string, index: number) => (
                              <li key={index}>• {concern}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {neuralPrep.aiInsights.trustBuilders && neuralPrep.aiInsights.trustBuilders.length > 0 && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <h5 className="font-medium text-blue-800 mb-2">Trust Builders</h5>
                          <ul className="text-xs text-blue-600 space-y-1">
                            {neuralPrep.aiInsights.trustBuilders.map((builder: string, index: number) => (
                              <li key={index}>• {builder}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Client History */}
              {neuralPrep.recentHistory && neuralPrep.recentHistory.length > 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                  <h3 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
                    📈 Recent Client History (Last {neuralPrep.recentHistory.length} Sessions)
                  </h3>
                  <div className="space-y-4">
                    {neuralPrep.recentHistory.map((session: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-amber-100">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-amber-800">{session.title}</h4>
                            <p className="text-sm text-amber-600">{session.date}</p>
                          </div>
                          {session.sentiment && (
                            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                              session.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                              session.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {session.sentiment}
                            </div>
                          )}
                        </div>
                        
                        {session.entities && session.entities.length > 0 && (
                          <div className="mb-3">
                            <h5 className="text-xs font-medium text-amber-700 mb-2">Key Entities:</h5>
                            <div className="flex flex-wrap gap-1">
                              {session.entities.map((entity: string, entityIndex: number) => (
                                <span key={entityIndex} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  👤 {entity}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {session.topics && session.topics.length > 0 && (
                          <div className="mb-3">
                            <h5 className="text-xs font-medium text-amber-700 mb-2">Topics Discussed:</h5>
                            <div className="flex flex-wrap gap-1">
                              {session.topics.map((topic: string, topicIndex: number) => (
                                <span key={topicIndex} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                                  🏷️ {topic}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {session.keyPoints && session.keyPoints.length > 0 && (
                          <div>
                            <h5 className="text-xs font-medium text-amber-700 mb-2">Key Discussion Points:</h5>
                            <ul className="text-xs text-amber-600 space-y-1">
                              {session.keyPoints.map((point: string, pointIndex: number) => (
                                <li key={pointIndex} className="flex items-start gap-2">
                                  <div className="w-1 h-1 bg-amber-500 rounded-full mt-2"></div>
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No neural prep data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 